import React, { useState } from 'react'
import dayjs from 'dayjs'
// importa tu diccionario de traducciones
import traslation from '../../../components/modules/ui/traslation/traslationAdministracion.json'

export default function TableMovimientos({ data = [] }) {
  const [sortConfig, setSortConfig] = useState({ key: 'fecha', direction: 'asc' })

  const formatCLP = value =>
    value?.toLocaleString('es-CL', { minimumFractionDigits: 0 }) ?? '-'

  const tipoMap = {
    ingreso: 'Clientes',
    egreso: 'Proveedor',
    vinculos: 'Vinculación',
  }

  const obtenerAbreviatura = nombre =>
    traslation.documentosContables[nombre] || nombre

  // 1) ordenar
  const sortedData = [...data].sort((a, b) => {
    const { key, direction } = sortConfig
    if (!key) return 0
    const aVal = typeof a[key] === 'string' ? a[key].toLowerCase() : a[key]
    const bVal = typeof b[key] === 'string' ? b[key].toLowerCase() : b[key]
    if (aVal < bVal) return direction === 'asc' ? -1 : 1
    if (aVal > bVal) return direction === 'asc' ? 1 : -1
    return 0
  })

  // 2) agrupar por razón social
  const groups = sortedData.reduce((acc, mov) => {
    const key = mov.razonSocial || '—'
    if (!acc[key]) acc[key] = []
    acc[key].push(mov)
    return acc
  }, {})

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  if (!data.length) {
    return <div className="py-12 text-center text-muted font-sans">No hay datos</div>
  }

  // número total de columnas para el colspan del separador
  const TOTAL_COLS = 14

  return (
    <div
      className="h-[650px] overflow-y-auto overflow-x-auto border border-border rounded bg-surface font-sans text-[11px] sm:text-xs text-text leading-5"
    >
      <table
        className="table-report"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col /> {/* Tipo */}
          <col /> {/* Fecha */}
          <col style={{ width: '250px', minWidth: '250px' }} /> {/* Rut/Razón */}
          <col style={{ width: '200px' }} /> {/* Doc */}
          <col style={{ width: '200px' }} /> {/* Comp */}
          <col /> {/* Monto */}
          <col /> {/* Abono */}
          <col /> {/* Cargo */}
          <col /> {/* Saldo */}
          <col /> {/* Pago Tipo/Num */}
          <col /> {/* Fecha Pago */}
          <col /> {/* Banco */}
          <col /> {/* Descripción */}
          <col /> {/* Status */}
        </colgroup>

        <thead className="sticky top-0 bg-surface z-10">
          <tr>
            <th colSpan={6} className="bg-border/40 p-1 text-center font-semibold text-text">
              Movimiento Documento/Vinculación
            </th>
            <th colSpan={8} className="bg-info/10 p-1 text-center font-semibold text-text">
              Movimiento Pago
            </th>
          </tr>
          <tr>
            {[
              { label: 'Tipo', key: 'types', color: 'bg-border/40' },
              { label: 'Fecha', key: 'fecha', color: 'bg-border/40' },
              { label: 'Rut / Razón Social', color: 'bg-border/40' },
              { label: 'Tipo Doc / N° Doc', color: 'bg-border/40' },
              { label: 'Tipo Comp. / N° Comp.', color: 'bg-border/40' },
              { label: 'Monto', key: 'montoTotal', color: 'bg-border/40' },
              { label: 'Abono', color: 'bg-info/10' },
              { label: 'Cargo', color: 'bg-info/10' },
              { label: 'Saldo', color: 'bg-info/10' },
              { label: 'Tipo Comp. / N° Comp.', color: 'bg-info/10' },
              { label: 'Fecha Pago', color: 'bg-info/10' },
              { label: 'Banco', color: 'bg-info/10' },
              { label: 'Descripción', color: 'bg-info/10' },
              { label: 'Status', key: 'status', color: 'bg-info/10' },
            ].map((col, idx) => (
              <th
                key={idx}
                className={`${col.color} p-1 text-left cursor-pointer select-none font-semibold text-text`}
                onClick={() => col.key && handleSort(col.key)}
              >
                {col.label}
                {col.key === sortConfig.key &&
                  (sortConfig.direction === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {Object.entries(groups).map(([razon, items]) => (
            <React.Fragment key={razon}>
              {/* fila separadora gris */}
              <tr className="bg-border/40">
                <td colSpan={TOTAL_COLS} className="p-1 font-medium text-text">
                  {razon}
                </td>
              </tr>

              {/* filas del grupo */}
              {items.map((mov, i) => {
                const totalAbono = (mov.linkage || []).reduce(
                  (sum, p) => sum + (p.montoAbono ?? 0),
                  0
                )
                const totalCargo = (mov.linkage || []).reduce(
                  (sum, p) => sum + (p.montoCargo ?? 0),
                  0
                )
                const movimiento = totalCargo > 0 ? totalCargo : totalAbono
                const tipoPago =
                  totalCargo > 0 ? 'egreso' : totalAbono > 0 ? 'ingreso' : ''
                const saldo = Math.abs(mov.montoTotal - movimiento)
                const firstLink = (mov.linkage || [])[0] || {}

                return (
                  <tr key={i} className="hover:bg-border/30">
                    <td className="p-1">{tipoMap[mov.types] || ''}</td>
                    <td className="p-1">
                      {dayjs(mov.fecha).format('DD/MM/YYYY')}
                    </td>

                    {/* Rut + Razón Social */}
                    <td className="p-1">
                      <div>{mov.rut}</div>
                      <div className="break-words">{mov.razonSocial}</div>
                    </td>

                    {/* Tipo Doc + Nº Doc */}
                    <td className="p-1">
                      <div>{obtenerAbreviatura(mov.nombreFolio)}</div>
                      <div>{mov.numeroFolio}</div>
                    </td>

                    {/* Tipo Comp. + N° Comp. */}
                    <td className="p-1">
                      <div>{mov.tipoComprobante}</div>
                      <div>{mov.comprobante}</div>
                    </td>

                    <td className="p-1 text-right">
                      {formatCLP(mov.montoTotal)}
                    </td>
                    <td className="p-1 text-right">
                      {formatCLP(totalAbono)}
                    </td>
                    <td className="p-1 text-right">
                      {formatCLP(totalCargo)}
                    </td>
                    <td className="p-1 text-right">
                      {formatCLP(saldo)}
                    </td>

                    {/* Tipo Pago + Nº Pago */}
                    <td className="p-1">
                      <div>{tipoPago || '-'}</div>
                      <div>{firstLink.comprobante ?? '-'}</div>
                    </td>

                    <td className="p-1">
                      {firstLink.fechaPago
                        ? dayjs(firstLink.fechaPago).format('DD/MM/YYYY')
                        : '-'}
                    </td>
                    <td className='p-1'>{firstLink.nombreCuentaBanco}</td>
                    <td className="p-1">{firstLink.descripcion || '-'}</td>
                    <td className="p-1">{mov.status || '-'}</td>
                  </tr>
                )
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
